import { useCallback, useEffect, useRef } from 'react';
import { useLocationContext, type LocationState } from '../store/LocationContext';
import { showError } from '../components/Errors/ErrorUtils';
import { useIonAlert } from '@ionic/react';

let data = new Array<LocationState>();

export const useUserLocation = (): void => {
  const { userLocation, setUserLocation } = useLocationContext();
  const coordIndex = useRef(0);
  const watchIdRef = useRef<number | null>(null);
  const ionAlert = useIonAlert();

  const simulateLocation = useCallback(() => {
    setUserLocation(data[coordIndex.current % data.length]);
    // console.log('Moving to ', data[coordIndex.current]);
    coordIndex.current = (coordIndex.current + 1) % data.length;
    setTimeout(simulateLocation, 1000); // Update every second
  }, [setUserLocation]);

  useEffect(() => {
    if (!import.meta.env.DEV) return;
    data = generateRouteJSON(routeCoordinates);
    setUserLocation(data[coordIndex.current]);
    coordIndex.current = (coordIndex.current + 1) % data.length;
    setTimeout(simulateLocation, 1000); // Start after 1 second
  }, [setUserLocation, simulateLocation]);

  const handlePositionSuccess = useCallback(
    (position: GeolocationPosition): void => {
      const { latitude: newLatitude, longitude: newLongitude } = position.coords;

      if (userLocation.latitude !== null && userLocation.longitude !== null) {
        const dist = Math.sqrt((newLatitude - userLocation.latitude) ** 2 + (newLongitude - userLocation.longitude) ** 2);
        if (dist < 0.000031) {
          return;
        }
      }

      if (!import.meta.env.DEV) {
        setUserLocation({
          accuracy: position.coords.accuracy,
          altitude: position.coords.altitude,
          altitudeAccuracy: position.coords.altitudeAccuracy,
          heading: position.coords.heading,
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          speed: position.coords.speed,
          timestamp: position.timestamp
        });
      }
    },
    [setUserLocation, userLocation]
  );

  const handlePositionError = useCallback((error: GeolocationPositionError): void => {
    console.error('Error watching position: ', error);
    // showError(ionAlert, "La géolocalisation n'est pas prise en charge ou n'est pas activée. Veuillez autoriser l'accès dans votre navigateur.");
  }, [ionAlert]);

  const startWatchPosition = useCallback((): void => {
    if (watchIdRef.current !== null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
    }

    watchIdRef.current = navigator.geolocation.watchPosition(handlePositionSuccess, handlePositionError, { enableHighAccuracy: true });
  }, [handlePositionError, handlePositionSuccess]);

  useEffect(() => {
    const checkPermissionAndStartWatch = (): void => {
      if ('permissions' in navigator) {
        void navigator.permissions.query({ name: 'geolocation' as PermissionName }).then((result) => {
          if (result.state === 'granted' || result.state === 'prompt') {
            startWatchPosition();
          } else if (result.state === 'denied') {
            // showError(ionAlert, "La géolocalisation est désactivée. Veuillez l'activer dans les paramètres de votre navigateur.");
          }

          result.onchange = () => {
            if (result.state === 'granted' || result.state === 'prompt') {
              startWatchPosition();
            } else if (result.state === 'denied') {
              if (watchIdRef.current !== null) {
                navigator.geolocation.clearWatch(watchIdRef.current);
                watchIdRef.current = null;
              }
            }
          };
        });
      } else {
        if ('geolocation' in navigator) {
          startWatchPosition();
        } else {
          // showError(ionAlert, "La géolocalisation n'est pas prise en charge par ce navigateur.");
        }
      }
    };

    checkPermissionAndStartWatch();

    return () => {
      if (watchIdRef.current !== null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
      }
    };
  }, [ionAlert, startWatchPosition]);
};

const routeCoordinates = [
  [6.141637, 46.210635],
  [6.141056, 46.210112],
  [6.140907, 46.209912],
  [6.140646, 46.20963],
  [6.140033, 46.208891],
  [6.140463, 46.208645],
  [6.140615, 46.208587],
  [6.1407, 46.208563],
  [6.140934, 46.20852],
  [6.141088, 46.208501],
  [6.142968, 46.208312],
  [6.144695, 46.208112],
  [6.14526, 46.208058],
  [6.145593, 46.208035],
  [6.145893, 46.207916],
  [6.147007, 46.207324],
  [6.147704, 46.206941],
  [6.147951, 46.206694],
  [6.149631, 46.20493],
  [6.149726, 46.204727],
  [6.15003, 46.204391],
  [6.15022, 46.204196],
  [6.150333, 46.204099],
  [6.150528, 46.203962],
  [6.150618, 46.203908],
  [6.150816, 46.203855],
  [6.151052, 46.203812],
  [6.151412, 46.203771],
  [6.153051, 46.203429],
  [6.155137, 46.202966],
  [6.156597, 46.202663],
  [6.156711, 46.202629],
  [6.157375, 46.202369],
  [6.159131, 46.201648],
  [6.162633, 46.20024],
  [6.162786, 46.20014],
  [6.163065, 46.200019],
  [6.163166, 46.199989],
  [6.163307, 46.199961],
  [6.163416, 46.199963],
  [6.163636, 46.19999],
  [6.165075, 46.200005],
  [6.165285, 46.19999],
  [6.167159, 46.200009],
  [6.1673, 46.2],
  [6.167678, 46.199958],
  [6.168499, 46.199965],
  [6.168777, 46.199974],
  [6.169119, 46.199998],
  [6.170088, 46.200019],
  [6.171053, 46.200032],
  [6.171396, 46.200018],
  [6.171752, 46.199991],
  [6.172019, 46.199957],
  [6.174172, 46.199629],
  [6.174318, 46.199595],
  [6.174868, 46.19949],
  [6.17613, 46.199274],
  [6.176937, 46.199161],
  [6.179343, 46.198917],
  [6.18034, 46.198804],
  [6.181908, 46.198657],
  [6.182353, 46.198603],
  [6.182701, 46.19858],
  [6.185072, 46.198337],
  [6.186218, 46.198129],
  [6.186996, 46.197923],
  [6.187349, 46.197816],
  [6.187829, 46.197653],
  [6.188427, 46.197422],
  [6.188897, 46.19722],
  [6.188944, 46.197204],
  [6.189062, 46.197184],
  [6.190073, 46.196519],
  [6.190482, 46.196261],
  [6.190805, 46.196096],
  [6.192627, 46.195253],
  [6.192661, 46.195216],
  [6.192698, 46.195199],
  [6.192772, 46.195194],
  [6.193304, 46.19493],
  [6.193862, 46.194601],
  [6.194565, 46.194299],
  [6.194701, 46.194261],
  [6.19491, 46.194223],
  [6.195269, 46.194134],
  [6.19582, 46.194019],
  [6.196676, 46.193859],
  [6.19843, 46.193572],
  [6.19974, 46.193335],
  [6.200764, 46.193162],
  [6.201233, 46.193102],
  [6.20202, 46.192968],
  [6.202306, 46.192905],
  [6.202735, 46.192833],
  [6.202982, 46.19301],
  [6.204042, 46.194053],
  [6.20454, 46.194569],
  [6.205174, 46.195203],
  [6.205503, 46.195514],
  [6.206122, 46.196059],
  [6.20628, 46.196172],
  [6.206634, 46.196386],
  [6.206947, 46.196541],
  [6.207698, 46.196865],
  [6.207873, 46.196922],
  [6.208816, 46.197257],
  [6.210287, 46.197752],
  [6.210727, 46.19791],
  [6.211162, 46.198054],
  [6.211423, 46.198148],
  [6.211705, 46.198261],
  [6.211989, 46.198412],
  [6.212661, 46.19883],
  [6.215487, 46.200618],
  [6.216561, 46.201283],
  [6.216889, 46.201471],
  [6.217207, 46.201641],
  [6.217566, 46.201817],
  [6.217977, 46.202],
  [6.218953, 46.202405],
  [6.222928, 46.203963],
  [6.22346, 46.204197],
  [6.22381, 46.204402],
  [6.22403, 46.20455],
  [6.224186, 46.204665],
  [6.22453, 46.204956],
  [6.224765, 46.205185],
  [6.225029, 46.205489],
  [6.225195, 46.205704],
  [6.225398, 46.20601],
  [6.225644, 46.206399],
  [6.22582, 46.206741],
  [6.226017, 46.207166],
  [6.226783, 46.208985],
  [6.226939, 46.209327],
  [6.227178, 46.209779],
  [6.227383, 46.21012],
  [6.227585, 46.21042],
  [6.227798, 46.210709],
  [6.227987, 46.210943],
  [6.228315, 46.21131],
  [6.22857, 46.211578],
  [6.228626, 46.211563],
  [6.228686, 46.211566],
  [6.228739, 46.211584],
  [6.228779, 46.211614],
  [6.228799, 46.211659],
  [6.228798, 46.211683],
  [6.228771, 46.211729],
  [6.228744, 46.211749],
  [6.229158, 46.212099],
  [6.229689, 46.212507],
  [6.229961, 46.212698],
  [6.230537, 46.213062],
  [6.230841, 46.213235],
  [6.231457, 46.213555],
  [6.231907, 46.213766],
  [6.233916, 46.214648],
  [6.233979, 46.214608],
  [6.23402, 46.214599],
  [6.234104, 46.214605],
  [6.234141, 46.214621],
  [6.234172, 46.214643],
  [6.234191, 46.214671],
  [6.234198, 46.214701],
  [6.234192, 46.214732],
  [6.234174, 46.21476],
  [6.23436, 46.214845],
  [6.234944, 46.215134],
  [6.235343, 46.215347],
  [6.235572, 46.215485],
  [6.235822, 46.215648],
  [6.236154, 46.215884],
  [6.236453, 46.216122],
  [6.236833, 46.216472],
  [6.237091, 46.216737],
  [6.237481, 46.217221],
  [6.23869, 46.218962],
  [6.23895, 46.219297],
  [6.239213, 46.219575],
  [6.239513, 46.219849],
  [6.239727, 46.220021],
  [6.239915, 46.220157],
  [6.240182, 46.220332],
  [6.240824, 46.22067],
  [6.242078, 46.221425],
  [6.242586, 46.221721],
  [6.242783, 46.221848],
  [6.243055, 46.222053],
  [6.243472, 46.22247],
  [6.244401, 46.223433],
  [6.244569, 46.223647],
  [6.245015, 46.224292],
  [6.245435, 46.22485],
  [6.245513, 46.224967],
  [6.245716, 46.225338],
  [6.246202, 46.226316],
  [6.246584, 46.226937],
  [6.246629, 46.22693],
  [6.246675, 46.226931],
  [6.246718, 46.226942],
  [6.246755, 46.226961],
  [6.246782, 46.226986],
  [6.246792, 46.227015]
];

function generateRouteJSON(routeCoordinates: string | any[]) {
  const altitudeGeneve = 375; // Altitude moyenne de Genève en mètres
  const accuracyMax = 5; // Précision GPS en mètres
  const speedKmH = 25; // Vitesse fixée en km/h
  const speedMS = speedKmH / 3.6; // Conversion en m/s

  function haversine(lon1: number, lat1: number, lon2: number, lat2: number) {
    const R = 6371000; // Rayon de la Terre en mètres
    const toRad = (deg: number) => (deg * Math.PI) / 180;
    const phi1 = toRad(lat1);
    const phi2 = toRad(lat2);
    const deltaPhi = toRad(lat2 - lat1);
    const deltaLambda = toRad(lon2 - lon1);

    const a = Math.sin(deltaPhi / 2) ** 2 + Math.cos(phi1) * Math.cos(phi2) * Math.sin(deltaLambda / 2) ** 2;
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c; // Distance en mètres
  }

  function calculateHeading(lon1: number, lat1: any, lon2: number, lat2: any) {
    const toRad = (deg: number) => (deg * Math.PI) / 180;
    const deltaLon = toRad(lon2 - lon1);
    const phi1 = toRad(lat1);
    const phi2 = toRad(lat2);

    const x = Math.sin(deltaLon) * Math.cos(phi2);
    const y = Math.cos(phi1) * Math.sin(phi2) - Math.sin(phi1) * Math.cos(phi2) * Math.cos(deltaLon);

    return (Math.atan2(x, y) * (180 / Math.PI) + 360) % 360; // Convertir en degrés et normaliser
  }

  function interpolatePoints(lon1: number, lat1: number, lon2: number, lat2: number, numPoints: number) {
    const points = [];
    const deltaLon = (lon2 - lon1) / numPoints;
    const deltaLat = (lat2 - lat1) / numPoints;

    for (let i = 1; i < numPoints; i++) {
      points.push([lon1 + deltaLon * i, lat1 + deltaLat * i]);
    }

    return points;
  }

  let currentTimestamp = Date.now(); // Timestamp actuel en millisecondes
  const jsonData = [];

  for (let i = 0; i < routeCoordinates.length; i++) {
    const [lon, lat] = routeCoordinates[i];

    let heading = null;
    if (i > 0) {
      const [prevLon, prevLat] = routeCoordinates[i - 1];
      const distance = haversine(prevLon, prevLat, lon, lat);
      const duration = distance / speedMS; // Durée en secondes
      const numPoints = Math.ceil(duration); // Nombre de points intermédiaires

      const interpolatedPoints = interpolatePoints(prevLon, prevLat, lon, lat, numPoints);

      for (let j = 0; j < interpolatedPoints.length; j++) {
        const [interLon, interLat] = interpolatedPoints[j];
        currentTimestamp += 1000; // Ajouter 1 seconde
        jsonData.push({
          accuracy: accuracyMax,
          altitude: altitudeGeneve,
          altitudeAccuracy: null,
          heading: calculateHeading(prevLon, prevLat, interLon, interLat),
          latitude: interLat,
          longitude: interLon,
          speed: speedMS,
          timestamp: currentTimestamp
        });
      }

      currentTimestamp += 1000; // Ajouter 1 seconde pour le point final
      heading = calculateHeading(prevLon, prevLat, lon, lat);
    }

    jsonData.push({
      accuracy: accuracyMax,
      altitude: altitudeGeneve,
      altitudeAccuracy: null,
      heading,
      latitude: lat,
      longitude: lon,
      speed: speedMS,
      timestamp: currentTimestamp
    });
  }

  return jsonData;
}
